import "semantic-ui-css/semantic.min.css";
import React, { useState, useEffect, useRef } from "react";
import customHttp from "../../services/customHTTP"; // Import your custom HTTP service
import "./GlobalSearch.css";
import { useSite } from "../../context/siteContext";
import {
	Box,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { ASSETS_BASE_URL } from "../../config";

// Assuming your routes are set up using useRoutes or a routes array
const routes = [
	{
		name: "Home",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/home",
		category: "Pages",
	},
	{
		name: "Machines",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/machines",
		category: "Pages",
	},
	{
		name: "Order History",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/order-history",
		category: "Pages",
	},
	{
		name: "Cart",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/cart",
		category: "Pages",
	},
	{
		name: "Profile",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/profile",
		category: "Pages",
	},
	{
		name: "Recommended Spare Parts List",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/rspl",
		category: "Pages",
	},
	{
		name: "Recommendations",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/recommendations",
		category: "Pages",
	},
	{
		name: "Requests",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/requests",
		category: "Pages",
	},
	{
		name: "Saved For Later",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/saved_for_later",
		category: "Pages",
	},
	{
		name: "Documents",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/documents",
		category: "Pages",
	},
	{
		name: "Contact CCR",
		sub_name: "",
		product_id: "",
		machine_id: "",
		image: `${ASSETS_BASE_URL}/stock.png`,
		href: "/contact-ccr",
		category: "Pages",
	},
	// Add more routes here as needed
];

const GlobalSearch = ({ width = "100%" }) => {
	const [machines, setMachines] = useState([]); // State to store Machines
	const [products, setProducts] = useState([]); // State to store Products
	const { selectedSite } = useSite();
	const [searchTerm, setSearchTerm] = useState("");
	const [data, setData] = useState([]); // State to store Products
	const [filteredData, setFilteredData] = useState(data);
	const navigate = useNavigate();
	const [isFocused, setIsFocused] = useState(false);
	const containerRef = useRef(null);
	const [resultsLoading, setResultsLoading] = useState(false);
	// Fetch Machines and Products data from API using customHttp
	const fetchData = async () => {
		try {
			const reqBody = {
				site_number: selectedSite,
				// Additional filters can go here (e.g., selectedSite if needed)
			};
			// Fetch Machines
			const machinesResponse = await customHttp.post("/machines/bysiteid", reqBody);
			const macs = machinesResponse.data.data.map((item) => {
				return {
					name: item.machine_name,
					product_id: "",
					machine_id: item.machine_id,
					sub_name: item.machine_number,
					image: `${ASSETS_BASE_URL}/MachineImages/${item.machine_number}.png`,
					href: `/machines/${item.machine_id}`,
					category: "Machines",
				};
			});
			setMachines(macs); // Assuming your API returns data in the 'data' key

			// Fetch Products
			const productsResponse = await customHttp.post("/products/bysiteid", reqBody);
			const procs = productsResponse.data.data.map((item) => {
				return {
					name: item.product_name,
					sub_name: item.product_number,
					product_id: item.product_id,
					machine_id: item.machine_id,
					image: `${ASSETS_BASE_URL}/ProductImages/TMB/${item.product_name}.JPG`,
					href: `/machines/${item.machine_id}/${item.product_id}`,
					category: "Products",
				};
			});
			setProducts(procs);
		} catch (error) {
			console.error("Error fetching data", error);
		}
	};

	useEffect(() => {
		fetchData(); // Fetch Machines and Products from API
		// eslint-disable-next-line
	}, [selectedSite]);

	useEffect(() => {
		setData([...products, ...machines, ...routes]);
	}, [products, machines]);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);
		setResultsLoading(true);

		// Filter the data based on the search term
	};

	useEffect(() => {
		if (searchTerm === "") {
			setFilteredData(data);
		} else {
			if (searchTerm.length >= 2) {
				const filtered = data.filter(
					(item) =>
						item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
						item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
						item.sub_name.toLowerCase().includes(searchTerm.toLowerCase()) // Optional: Filter by category
				);
				setFilteredData(filtered);
			}
		}
	}, [searchTerm]);

	const handleItemClick = (item) => {
		setIsFocused(false);
		navigate(`${item.href}`);
		// You can perform any action on click here, like navigating or showing details
	};

	const handleImgError = (e) => {
		const defaultImage = `${ASSETS_BASE_URL}/stock.png`;
		e.target.onerror = null; // Prevent infinite loop
		e.target.src = defaultImage; // Fallback to default image
	};

	// Group filtered data by category
	const categorizedData = {
		Pages: filteredData.filter((item) => item.category === "Pages"),
		Products: filteredData.filter((item) => item.category === "Products"),
		Machines: filteredData.filter((item) => item.category === "Machines"),
	};

	useEffect(() => {
		if (filteredData.length >= 0) {
			setResultsLoading(false);
		}
	}, [filteredData]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsFocused(false);
				setSearchTerm("");
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div className="search-container" style={{ width }} ref={containerRef}>
			<TextField
				variant="outlined"
				placeholder="Search"
				fullWidth
				value={searchTerm}
				onChange={handleSearchChange}
				onFocus={() => setIsFocused(true)}
				size="small"
				slotProps={{
					input: {
						endAdornment: (
							<Box display="flex">
								<IconButton marginRight="-2px">
									<SearchIcon />
								</IconButton>
							</Box>
						),
					},
				}}
				sx={{
					"& .MuiInputBase-root": {
						paddingRight: "2px", // Ensure there's no extra padding in the input field itself
					},
					"& .MuiOutlinedInput-root": {
						borderRadius: "40px",
						backgroundColor: "#fff",
					},
					width: "100%",
				}}
			/>

			{/* The dropdown-style container for search results */}
			{isFocused && searchTerm && (
				<Paper className="search-results">
					{resultsLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								padding: "10px",
								flexDirection: "row",
								columnGap: "10px",
							}}>
							<CircularProgress color="inherit" size="25px" />
							<Typography variant="body1" fontSize="11px">
								Loading
							</Typography>
						</Box>
					) : (
						<List>
							{/* Display Pages Results */}
							{categorizedData.Pages.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										Pages
									</Typography>
									{categorizedData.Pages.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												onError={handleImgError}
												className="result-image"
											/>
											<div className="result-info">
												<ListItemText primary={item.name} />
												<div className="category-text">{item.sub_name}</div>
											</div>
										</ListItem>
									))}
								</>
							)}

							{/* Display Products Results */}
							{categorizedData.Products.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										Parts
									</Typography>
									{categorizedData.Products.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												className="result-image"
												onError={handleImgError}
											/>
											<div className="result-info">
												<ListItemText primary={item.name} />
												<div className="category-text">Part #: {item.sub_name}</div>
											</div>
										</ListItem>
									))}
								</>
							)}

							{/* Display Machines Results */}
							{categorizedData.Machines.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										Machines
									</Typography>
									{categorizedData.Machines.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												className="result-image"
												onError={handleImgError}
											/>
											<div className="result-info">
												<ListItemText primary={item.name} />
												<div className="category-text">Machine #: {item.sub_name}</div>
											</div>
										</ListItem>
									))}
								</>
							)}

							{/* Display message when no results are found */}
							{filteredData.length === 0 && (
								<ListItem className="no-results">
									<ListItemText primary="No results found" />
								</ListItem>
							)}
						</List>
					)}
				</Paper>
			)}
		</div>
	);
};

export default GlobalSearch;
